import { type FC, type MouseEvent, type RefObject } from 'react';
import cx from 'classnames';

import { ChevronIcon } from 'src/components/ChevronIcon';
// @ts-expect-error - ContextMenu doesn't have TypeScript definitions
import { ContextMenu } from 'src/components/ContextMenu';
import {
  CUSTOM_BUTTON_SIZE,
  CUSTOM_BUTTON_VARIANT,
  CustomButton,
  // @ts-expect-error - CustomButton doesn't have TypeScript definitions
} from 'src/components/CustomButton';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
// @ts-expect-error - FieldSelectMenuItem doesn't have TypeScript definitions
import { FieldSelectMenuItem } from 'src/components/FieldSelectMenuItem';
import {
  getProjectTypeDescription,
  getProjectTypeLabel,
  PROJECT_TYPE,
  type ProjectType,
} from 'src/data/project';
import { ROTATE_DIRECTION } from 'src/data/ui';
import { type Callback, type VoidFunction } from 'src/lib/types';

import styles from './ProjectTypeDropdown.module.scss';

const PROJECT_TYPES = [PROJECT_TYPE.document, PROJECT_TYPE.longFormBuilder];

const MENU_PROPS = {
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
  getContentAnchorEl: null,
};

interface ProjectTypeDropdownProps {
  className?: string;
  value: ProjectType;
  onChange: Callback<ProjectType>;
  customDescriptions?: Partial<Record<ProjectType, string>>;
  buttonTestId: string;
}

interface ButtonProps {
  open: boolean;
  ref: RefObject<HTMLButtonElement>;
  onClick: VoidFunction;
}

export const ProjectTypeDropdown: FC<ProjectTypeDropdownProps> = ({
  className,
  value,
  onChange,
  customDescriptions,
  buttonTestId,
}) => {
  return (
    <ContextMenu
      classes={{ menuRoot: styles.menuRoot }}
      menuProps={MENU_PROPS}
      renderButton={(props: ButtonProps) => (
        <CustomButton
          className={cx(styles.root, className)}
          classes={{ label: styles.label }}
          variant={CUSTOM_BUTTON_VARIANT.text}
          size={CUSTOM_BUTTON_SIZE.smaller}
          data-testid={buttonTestId}
          {...props}
        >
          {getProjectTypeLabel(value)}
          <ChevronIcon
            className={styles.iconChevron}
            direction={props.open ? ROTATE_DIRECTION.up : ROTATE_DIRECTION.down}
          />
        </CustomButton>
      )}
      renderItems={(closeMenu: Callback<MouseEvent>) =>
        PROJECT_TYPES.map((projectType) => (
          <FieldSelectMenuItem
            className={styles.item}
            classes={{ selected: styles.selectedItem }}
            key={projectType}
            value={projectType}
            selected={projectType === value}
            data-testid={`projectTypeOption_${projectType}`}
            onClick={(event: MouseEvent) => {
              onChange(projectType);
              closeMenu(event);
            }}
          >
            <CustomTypography
              className={styles.title}
              variant={TYPOGRAPHY_VARIANT.regular14}
            >
              {getProjectTypeLabel(projectType)}
            </CustomTypography>

            <CustomTypography
              className={styles.description}
              variant={TYPOGRAPHY_VARIANT.regular12}
            >
              {customDescriptions?.[projectType] || getProjectTypeDescription(projectType)}
            </CustomTypography>
          </FieldSelectMenuItem>
        ))
      }
    />
  );
};
